import './tooltip.scss';

import { ReactNode, useRef } from 'react';

import { locator } from '../../testing/e2e-locator/locator';
import { useExitAnimation } from '../hooks/useExitAnimation';
import { TOOLTIP_EXIT_DURATION } from './constants/tooltipTiming';
import { useTooltipPosition } from './hooks/useTooltipPosition';
import { useTooltipTriggers } from './hooks/useTooltipTriggers';
import { TooltipProps } from './types/TooltipProps';

export const Tooltip = ({ children, title }: TooltipProps): ReactNode => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);
  const popperRef = useRef<HTMLSpanElement>(null);
  const {
    handleBlur,
    handleFocus,
    handleMouseLeave,
    handleMouseOver,
    handleTouchEnd,
    handleTouchStart,
    open,
    touch,
  } = useTooltipTriggers({ popperRef });
  const { content, handleAnimationEnd, visible } = useExitAnimation({
    children: title,
    exitDuration: TOOLTIP_EXIT_DURATION,
    open,
  });
  const { arrowLeft, left, placement } = useTooltipPosition({
    anchorRef,
    arrowRef,
    open,
    popperRef,
  });

  const className = [
    'tooltip-popper',
    `tooltip-${placement}`,
    touch && 'tooltip-touch',
    !open && 'tooltip-exiting',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // The popper sits inside the wrapper so hovering it keeps the tooltip open
    <span
      className="tooltip"
      ref={anchorRef}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
    >
      {children}
      {visible && (
        <span
          className={className}
          ref={popperRef}
          style={{ left }}
          onAnimationEnd={handleAnimationEnd}
        >
          <span
            className="tooltip-popup"
            data-testid={locator.components.tooltip.popup}
            role="tooltip"
          >
            {content}
            <span
              className="tooltip-arrow"
              ref={arrowRef}
              style={{ left: arrowLeft }}
            />
          </span>
        </span>
      )}
    </span>
  );
};
